import { useCallback, useRef, useState } from 'react'

type SearchValues<T> = Record<keyof T, string>

export function useSearchState<T extends Record<string, string | undefined>>(initial: T) {
  const [values, setValues] = useState<SearchValues<T>>(
    () =>
      Object.fromEntries(
        Object.keys(initial).map((key) => [key, initial[key] ?? '']),
      ) as SearchValues<T>,
  )

  const setters = useRef<Partial<Record<keyof T, (value: string) => void>>>({})

  const setSearch = useCallback((key: keyof T) => {
    if (!setters.current[key]) {
      setters.current[key] = (value: string) =>
        setValues((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }))
    }

    return setters.current[key] as (value: string) => void
  }, [])

  return [values, setSearch] as const
}
